import * as Yup from 'yup';
import { useEffect } from 'react';

import { Box, Button, CircularProgress } from '@mui/material';

import {
  getKeycloakClientId,
  getKeycloakRealm,
  getKeycloakUrl,
  useSsoSdkBypass,
} from 'services/HostService/HostService';
import { Common } from 'utils';

import { keycloak } from '../../keycloak';
import { cardLoginStyle, containerStyle } from './LoginPage.styles';

export const validators = Yup.object().shape({
  username: Yup.string().required('* Digite seu usuário'),
  password: Yup.string().required('* Digite sua senha'),
});

const PKCE_VERIFIER_STORAGE_KEY = 'sso_pkce_verifier';
const PKCE_STATE_STORAGE_KEY = 'sso_pkce_state';

const toBase64Url = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const createRandomHex = (size: number): string => {
  const randomBytes = new Uint8Array(size);
  window.crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
};

const createCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return toBase64Url(digest);
};

export default function LoginPage(): JSX.Element {
  const bypassSdkValidation = useSsoSdkBypass();

  useEffect(() => {
    const session = Common.getSessionValue('logCookie');
    if (session?.includes('name')) {
      window.location.replace('/app/new-request');
    }
  }, []);

  const handleLogin = async () => {
    if (!bypassSdkValidation) {
      keycloak.login();
      return;
    }

    const keycloakUrl = getKeycloakUrl();
    const realm = getKeycloakRealm();
    const clientId = getKeycloakClientId();

    if (!keycloakUrl || !realm || !clientId) {
      return;
    }

    const codeVerifier = createRandomHex(32);
    const state = createRandomHex(16);
    const codeChallenge = await createCodeChallenge(codeVerifier);

    sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, codeVerifier);
    sessionStorage.setItem(PKCE_STATE_STORAGE_KEY, state);
    localStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, codeVerifier);
    localStorage.setItem(PKCE_STATE_STORAGE_KEY, state);

    const authBase = `${keycloakUrl.replace(
      /\/$/,
      '',
    )}/realms/${realm}/protocol/openid-connect/auth`;
    const callback = window.location.origin;
    const authUrl =
      `${authBase}?client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(callback)}` +
      '&response_type=code' +
      '&response_mode=query' +
      '&scope=openid' +
      `&state=${encodeURIComponent(state)}` +
      `&code_challenge=${encodeURIComponent(codeChallenge)}` +
      '&code_challenge_method=S256';

    window.location.assign(authUrl);
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={cardLoginStyle}>
        <Box sx={{ mb: 2 }}>
          <img src="/assets/logo-impulse.webp" alt="Logo Impulse" width={264} />
        </Box>
        <Button
          sx={{ mt: 2 }}
          size="large"
          fullWidth
          variant="outlined"
          color="primary"
          onClick={handleLogin}
        >
          <CircularProgress size="23px" />
          &nbsp;&nbsp;Carregando
        </Button>
      </Box>
    </Box>
  );
}
